import { useCallback, useEffect, useState } from "react";
import { accountApi } from "../services/accountApi";

const ROLE_RANK = { Viewer: 0, Contributor: 1, Limited: 2, Editor: 3, Admin: 4, Owner: 5 };

/**
 * Effective team role for the signed-in user, for gating Admin/Editor-only
 * controls on the Team pages (TeamTasksPage, TeamDashboardPage).
 *
 * Why this shape, not a global "acting account" concept:
 * This app has no account-switcher / "X-Acting-Account-Id" notion anywhere
 * (checked apiClient.js's request interceptor and AuthContext.jsx -- every
 * request just carries the caller's own bearer token). api/services/auth.py
 * resolves ctx.user_id purely by looking up the caller's email on every
 * request (_resolve_user_id) and never returns that numeric id to the
 * browser -- there is no "/me" endpoint and no other response in web/src
 * that exposes the signed-in user's own app.Users.user_id. Elsewhere in the
 * app (e.g. hiveDetail/OverviewTab.jsx) raw user_id values are just rendered
 * as-is with no "is this me" comparison, confirming the frontend has never
 * had that capability.
 *
 * Team pages show one combined list built from RLS across every account the
 * signed-in user can reach: their own account (where api/services/
 * team_access.py's get_effective_role always reports "Owner") plus any
 * account someone else shared TO them (where it reports their
 * Admin/Editor/Viewer/Limited app.AccountShares role). Rows from different
 * owners are merged without an owner id/email the browser could match
 * against "me", so there's no reliable way to tell, per task/comment row,
 * which owner's rules apply.
 *
 * Rather than guess per row, this hook reuses the same accountApi.listShares()
 * call RoleManagementSection already makes and reports the MOST RESTRICTIVE
 * Approved role the user holds across accounts shared to them (defaults to
 * "Owner" -- full control -- when they have no received shares, which is the
 * common single-account case and is left completely unchanged). Pages use
 * `canCreate` / `canManage` as a conservative, page-level proxy for the
 * role rules api/functions/team.py actually enforces:
 *   - _assert_can_write blocks writes (create task/comment/handoff) only for
 *     role === "Viewer" -> canCreate.
 *   - Deleting a task, or deleting *someone else's* comment, requires
 *     role in ("Owner", "Admin") -> canManage.
 * This can occasionally hide a control on the user's own, unrestricted data
 * if they *also* hold a lesser role on some other shared account, but it
 * never shows a control the backend would actually reject -- which is the
 * direction that matters for closing this gap.
 *
 * Also returns `teamEmails`: the deduped list of Approved-share email
 * addresses (people you've shared your account with, plus owners of
 * accounts shared to you) -- the same data source, reused for the @mention
 * picker on TeamDashboardPage's comment box.
 */
export function useTeamRole() {
  const [role, setRole] = useState(null);
  const [teamEmails, setTeamEmails] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const shares = await accountApi.listShares();
      const received = shares?.received || [];
      const sent = shares?.sent || [];

      const approvedReceivedRoles = received.filter((s) => s.status === "Approved").map((s) => s.role);
      if (approvedReceivedRoles.length === 0) {
        setRole("Owner");
      } else {
        setRole(
          approvedReceivedRoles.reduce(
            (worst, r) => (ROLE_RANK[r] ?? 0) < (ROLE_RANK[worst] ?? 0) ? r : worst,
            approvedReceivedRoles[0]
          )
        );
      }

      const emails = new Set();
      for (const s of sent) if (s.status === "Approved" && s.shared_email) emails.add(s.shared_email);
      for (const s of received) if (s.status === "Approved" && s.primary_email) emails.add(s.primary_email);
      setTeamEmails([...emails]);
    } catch {
      // Can't determine role -- default to the most restrictive state
      // rather than risk showing a control that would actually 403.
      setRole("Viewer");
      setTeamEmails([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const canCreate = role !== "Viewer";
  const canManage = role === "Owner" || role === "Admin";

  return { role, loading, canCreate, canManage, teamEmails, refresh: load };
}
